// src/features/admin/authSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface AuthState {
  token: string | null;
  email: string | null;
  userId: string | null;
  userName: string | null;
  role: 'admin' | 'vendor' | null;
  adminType: string | null; // Added adminType
  isAuthenticated: boolean;
  vendorId: string | null;
}

const initialState: AuthState = {
  token: null,
  email: null,
  userId: null,
  userName: null,
  role: null,
  adminType: null, // Initialize adminType
  isAuthenticated: false,
  vendorId: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAdminCredentials: (state, action: PayloadAction<{
      token: string;
      email: string;
      userId: string;
      userName: string;
      role?: string;
      adminType?: string; // Added adminType
    }>) => {
      const adminType = action.payload.adminType || action.payload.role || 'admin';
      
      state.token = action.payload.token;
      state.email = action.payload.email;
      state.userId = action.payload.userId;
      state.userName = action.payload.userName;
      state.role = 'admin';
      state.adminType = adminType; // Set adminType
      state.isAuthenticated = true;
      state.vendorId = null;
      
      // Store in localStorage
      localStorage.setItem('admin_token', action.payload.token);
      localStorage.setItem('user_email', action.payload.email);
      localStorage.setItem('user_id', action.payload.userId);
      localStorage.setItem('user_name', action.payload.userName);
      localStorage.setItem('user_role', 'admin');
      localStorage.setItem('admin_type', adminType); // Store admin type
      localStorage.removeItem('vendor_token');
      localStorage.removeItem('vendor_id');
    },
    setVendorCredentials: (state, action: PayloadAction<{
      token: string;
      email: string;
      vendorId?: string | number;
      userName: string;
      role?: string;
    }>) => {
      // Convert vendorId to string and ensure it exists
      const vendorIdStr = action.payload.vendorId?.toString() || '';
      
      state.token = action.payload.token;
      state.email = action.payload.email;
      state.userId = vendorIdStr; // Use vendorId as userId for vendors
      state.userName = action.payload.userName;
      state.role = 'vendor';
      state.adminType = null; // Not applicable for vendors
      state.isAuthenticated = true;
      state.vendorId = vendorIdStr;
      
      console.log('Redux: Storing vendor credentials', {
        vendorId: vendorIdStr,
        userName: action.payload.userName,
        email: action.payload.email
      });
      
      // Store in localStorage
      localStorage.setItem('vendor_token', action.payload.token);
      localStorage.setItem('user_email', action.payload.email);
      localStorage.setItem('user_id', vendorIdStr);
      localStorage.setItem('user_name', action.payload.userName);
      localStorage.setItem('user_role', 'vendor');
      localStorage.setItem('vendor_id', vendorIdStr);
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_type');
    },
    clearCredentials: (state) => {
      state.token = null;
      state.email = null;
      state.userId = null;
      state.userName = null;
      state.role = null;
      state.adminType = null;
      state.isAuthenticated = false;
      state.vendorId = null;
      
      // Clear localStorage
      localStorage.removeItem('admin_token');
      localStorage.removeItem('vendor_token');
      localStorage.removeItem('user_role');
      localStorage.removeItem('user_email');
      localStorage.removeItem('user_id');
      localStorage.removeItem('user_name');
      localStorage.removeItem('vendor_id');
      localStorage.removeItem('admin_type');
    },
    initializeAuth: (state) => {
      const token = localStorage.getItem('vendor_token') || localStorage.getItem('admin_token');
      const role = localStorage.getItem('user_role') as 'admin' | 'vendor' | null;
      const email = localStorage.getItem('user_email');
      const userId = localStorage.getItem('user_id');
      const userName = localStorage.getItem('user_name');
      const vendorId = localStorage.getItem('vendor_id');
      const adminType = localStorage.getItem('admin_type');

      console.log('Initializing auth from localStorage:', {
        role,
        vendorId,
        userName,
        adminType,
        hasToken: !!token
      });

      if (token && role && email) {
        state.token = token;
        state.email = email;
        state.userId = userId;
        state.userName = userName;
        state.role = role;
        state.adminType = adminType;
        state.isAuthenticated = true;
        state.vendorId = role === 'vendor' ? vendorId : null;
      }
    },
    setAdminType: (state, action: PayloadAction<string>) => {
      state.adminType = action.payload;
      localStorage.setItem('admin_type', action.payload);
    },
  },
});

export const { 
  setAdminCredentials, 
  setVendorCredentials, 
  clearCredentials, 
  initializeAuth,
  setAdminType 
} = authSlice.actions;

export default authSlice.reducer;